import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck, PiggyBank, ArrowRight, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmt } from "@/lib/format";
import { journeyApi } from "@/lib/journeyApi";

// --------------------------------------------------------------------------
// Constants (mirror the backend allow-lists)
// --------------------------------------------------------------------------
export const INCOME_TYPE_CARDS = [
  {
    value: "predictable",
    title: "Predictable",
    tagline: "I get paid regularly.",
    examples: "Salary, regular paycheck, pension, or other reliably scheduled income.",
  },
  {
    value: "variable",
    title: "Variable",
    tagline: "My income changes.",
    examples: "Commission, real estate, freelance, contract work, consulting, gig work, creator income, or irregular owner pay.",
  },
  {
    value: "hybrid",
    title: "Hybrid",
    tagline: "I have both.",
    examples: "Salary plus commissions, bonuses, freelance income, side income, or other irregular money.",
  },
];

export const PAY_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "every_two_weeks", label: "Every two weeks" },
  { value: "twice_monthly", label: "Twice a month" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

export const ESSENTIAL_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "every_two_weeks", label: "Every two weeks" },
  { value: "twice_monthly", label: "Twice a month" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
  { value: "custom", label: "Custom" },
];

export const ESSENTIAL_CATEGORIES = [
  { value: "housing", label: "Housing" },
  { value: "utilities", label: "Utilities & Household" },
  { value: "transportation", label: "Transportation" },
  { value: "insurance", label: "Insurance & Health" },
  { value: "family", label: "Family" },
  { value: "other", label: "Other Essential" },
];

export const DEBT_TYPES = [
  { value: "credit_card", label: "Credit card" },
  { value: "personal_loan", label: "Personal loan" },
  { value: "medical", label: "Medical debt" },
  { value: "student_loan", label: "Student loan" },
  { value: "auto_loan", label: "Auto loan" },
  { value: "other", label: "Other consumer debt" },
];

export const VARIABLE_SOURCES = ["Commissions", "Bonuses", "Freelance", "Consulting", "Side income", "Other"];

// --------------------------------------------------------------------------
// Shared helpers
// --------------------------------------------------------------------------
export const uid = () => Math.random().toString(36).slice(2, 9);
export const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Standard calendar normalization to a monthly-equivalent, used ONLY for the
// live on-screen Essentials estimate during onboarding. The backend holds the
// canonical normalization used by the engine; this display figure mirrors the
// same standard period math and is labeled as an estimate.
const MONTHLY_FACTOR = {
  weekly: 52 / 12,
  every_two_weeks: 26 / 12,
  twice_monthly: 2,
  monthly: 1,
  quarterly: 1 / 3,
  annually: 1 / 12,
};
export function monthlyEquivalent(amount, frequency, customIntervalDays) {
  const a = num(amount);
  if (!a) return 0;
  if (frequency === "custom") {
    const d = parseInt(customIntervalDays, 10);
    return d && d > 0 ? a * (365.25 / 12 / d) : a;
  }
  return a * (MONTHLY_FACTOR[frequency] ?? 1);
}
export const blankEssential = () => ({
  id: uid(), category: "", name: "", amount: "", frequency: "",
  due_day: "", next_due_date: "", custom_interval_days: "",
});
export const blankDebt = () => ({
  id: uid(), debt_type: "", name: "", balance: "", minimum_payment: "",
  minimum_due_day: "", apr: "",
});

export function foundationToForm(f) {
  const inc = f?.income || {};
  const s = (v) => (v === null || v === undefined ? "" : String(v));
  return {
    income: {
      type: inc.type || "",
      take_home_paycheck: inc.take_home_paycheck ? s(inc.take_home_paycheck) : "",
      pay_frequency: inc.pay_frequency || "monthly",
      next_payday: inc.next_payday || "",
      income_timing_uncertain: !!inc.income_timing_uncertain,
      income_history_inconsistent: !!inc.income_history_inconsistent,
      no_other_household_income: !!inc.no_other_household_income,
      variable_sources: inc.variable_sources || [],
      regular_covers_essentials: inc.regular_covers_essentials || "",
    },
    essentials: (f?.essentials || []).map((e) => ({
      id: e.id || uid(), category: e.category || "other", name: e.name || "", amount: s(e.amount),
      frequency: e.frequency || "monthly", due_day: s(e.due_day), next_due_date: e.next_due_date || "",
      custom_interval_days: s(e.custom_interval_days),
    })),
    living_bucket: s(f?.living_bucket),
    safety: {
      emergency_savings: s(f?.safety?.emergency_savings),
      protected_runway: s(f?.safety?.protected_runway),
    },
    mortgage: {
      has_mortgage: !!f?.mortgage?.has_mortgage,
      balance: s(f?.mortgage?.balance),
      interest_rate: s(f?.mortgage?.interest_rate),
      term_months: s(f?.mortgage?.term_months),
      monthly_payment: s(f?.mortgage?.monthly_payment),
    },
    debts: (f?.debts || []).map((d) => ({
      id: d.id || uid(), debt_type: d.debt_type || "credit_card", name: d.name || "", balance: s(d.balance),
      minimum_payment: s(d.minimum_payment), minimum_due_day: s(d.minimum_due_day), apr: s(d.apr),
    })),
  };
}

export function buildPayload(form) {
  const inc = form.income;
  const isVariable = inc.type === "variable";
  // Hybrid: if the regular income would NOT cover essentials, that is treated as
  // "no other reliable household income" for the Protected Runway calculation.
  const noOther = inc.type === "hybrid"
    ? inc.regular_covers_essentials === "no"
    : !!inc.no_other_household_income;
  return {
    income: {
      type: inc.type,
      take_home_paycheck: isVariable ? 0 : num(inc.take_home_paycheck),
      pay_frequency: isVariable ? null : inc.pay_frequency,
      next_payday: inc.next_payday || null,
      income_timing_uncertain: !!inc.income_timing_uncertain,
      income_history_inconsistent: !!inc.income_history_inconsistent,
      no_other_household_income: noOther,
      variable_sources: inc.variable_sources || [],
      regular_covers_essentials: inc.regular_covers_essentials || null,
    },
    essentials: (form.essentials || [])
      .filter((e) => e.name.trim() && num(e.amount) > 0)
      .map((e) => ({
        id: e.id,
        category: e.category || "other",
        name: e.name.trim(),
        amount: num(e.amount),
        frequency: e.frequency || "monthly",
        due_day: (e.frequency || "monthly") === "monthly" && e.due_day ? parseInt(e.due_day, 10) : null,
        next_due_date: e.frequency && e.frequency !== "monthly" && e.next_due_date ? e.next_due_date : null,
        custom_interval_days: e.frequency === "custom" && e.custom_interval_days ? parseInt(e.custom_interval_days, 10) : null,
      })),
    living_bucket: num(form.living_bucket),
    safety: {
      emergency_savings: num(form.safety.emergency_savings),
      protected_runway: num(form.safety.protected_runway),
    },
    mortgage: {
      has_mortgage: !!(form.mortgage && form.mortgage.has_mortgage),
      balance: form.mortgage && form.mortgage.has_mortgage ? num(form.mortgage.balance) : 0,
      interest_rate: form.mortgage && form.mortgage.has_mortgage && form.mortgage.interest_rate !== "" ? parseFloat(form.mortgage.interest_rate) : null,
      term_months: form.mortgage && form.mortgage.has_mortgage && form.mortgage.term_months ? parseInt(form.mortgage.term_months, 10) : null,
      monthly_payment: form.mortgage && form.mortgage.has_mortgage && form.mortgage.monthly_payment ? num(form.mortgage.monthly_payment) : null,
    },
    debts: (form.debts || [])
      .filter((d) => d.name.trim())
      .map((d) => ({
        id: d.id,
        debt_type: d.debt_type || "other",
        name: d.name.trim(),
        balance: num(d.balance),
        minimum_payment: num(d.minimum_payment),
        minimum_due_day: d.minimum_due_day ? parseInt(d.minimum_due_day, 10) : null,
        apr: d.apr ? parseFloat(d.apr) : null,
      })),
  };
}

// --------------------------------------------------------------------------
// Field primitives
// --------------------------------------------------------------------------
const fieldCls =
  "mt-1 w-full rounded-xl pe-surface px-3.5 py-2.5 text-base text-[#171717] outline-none transition focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/30";
const labelCls = "text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]";

export function Money({ label, value, onChange, placeholder = "$0", testid, hint }) {
  return (
    <div>
      {label && <label className={labelCls}>{label}</label>}
      <input
        data-testid={testid}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        placeholder={placeholder}
        className={fieldCls}
      />
      {hint && <p className="mt-1 text-xs leading-relaxed text-[#A89A88]">{hint}</p>}
    </div>
  );
}

function StepHead({ eyebrow, title, children }) {
  return (
    <div>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">{eyebrow}</p>}
      <h1 className="mt-2 font-serif text-3xl leading-tight text-[#171717] sm:text-4xl">{title}</h1>
      {children && <div className="mt-3 text-[#525252]">{children}</div>}
    </div>
  );
}

// Small, consistent "why we ask" disclosure. Not a giant card, not a modal.
export function WhyWeAsk({ children, testid }) {
  return (
    <div className="rounded-xl border border-[#E5E0D9] bg-[#FBF9F4] p-3.5" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">Why we ask</p>
      <p className="mt-1 text-sm leading-relaxed text-[#70584A]">{children}</p>
    </div>
  );
}

// --------------------------------------------------------------------------
// 1. Welcome
// --------------------------------------------------------------------------
export function WelcomeStep({ firstName, onStart }) {
  return (
    <div className="space-y-6" data-testid="onboarding-welcome">
      <StepHead title={<>Hey, {firstName || "there"} <span className="align-middle">👋</span></>}>
        <p className="text-xl text-[#3f3f3f]">Let's build your Paycheck Edit.</p>
      </StepHead>
      <p className="text-base leading-relaxed text-[#525252]">
        First, we'll build your financial foundation. This helps The Paycheck Edit understand what may need to stay
        protected and what may be available when money lands.
      </p>
      <div className="rounded-2xl pe-surface p-5">
        <p className="font-medium text-[#171717]">Okay, this is the least exciting part of The Paycheck Edit. 😅</p>
        <p className="mt-2 text-sm leading-relaxed text-[#70584A]">
          We need the numbers before we can do the fun part. Give us a few minutes now, so when money lands later,
          The Paycheck Edit can get straight to the good stuff.
        </p>
      </div>
      <div className="rounded-2xl border border-[#C9A86A]/40 bg-[#FBF7EF] p-4" data-testid="onboarding-disclosure">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">Quick heads-up before we Edit</p>
        <p className="mt-1.5 text-sm leading-relaxed text-[#70584A]">
          The Paycheck Edit is an educational financial-planning tool. It uses the information you provide to
          calculate possible next moves, but it isn't your financial, investment, tax, accounting, legal, or credit
          adviser. You're always in control of what happens next. See our{" "}
          <button type="button" onClick={() => window.open("/legal#disclaimer", "_blank")} className="font-medium text-[#164C3B] underline">full disclaimer</button>.
        </p>
      </div>
      <Button
        data-testid="onboarding-start-button"
        onClick={onStart}
        className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]"
      >
        FINE, LET'S DO THE BORING PART
      </Button>
    </div>
  );
}

// --------------------------------------------------------------------------
// 2. Income type
// --------------------------------------------------------------------------
export function IncomeTypeStep({ form, setIncome }) {
  const selected = form.income.type;
  return (
    <div className="space-y-6" data-testid="onboarding-income-type">
      <StepHead eyebrow="Income" title="How does your income usually show up?">
        <p>Pick the one that fits best. You can change this later from your profile.</p>
      </StepHead>
      <div className="space-y-3">
        {INCOME_TYPE_CARDS.map((c) => {
          const active = selected === c.value;
          return (
            <button
              key={c.value}
              type="button"
              data-testid={`income-type-${c.value}`}
              onClick={() => setIncome({ type: c.value })}
              className={`w-full rounded-2xl border-2 bg-white p-5 text-left transition ${
                active ? "border-[#2F6B57] ring-2 ring-[#2F6B57]/20" : "border-[#E5E0D9] hover:border-[#C9A86A]"
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="font-serif text-xl text-[#171717]">{c.title}</p>
                {active && <CheckCircle2 className="h-5 w-5 text-[#2F6B57]" />}
              </div>
              <p className="mt-1 text-sm font-medium text-[#164C3B]">{c.tagline}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#70584A]">Examples: {c.examples}</p>
              {c.value === "hybrid" && active && (
                <p className="mt-2 text-sm italic text-[#8A7C6B]">A little predictable. A little unpredictable. We got you. 😅</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// 3/4/5. Income details (branches)
// --------------------------------------------------------------------------
function PredictableFields({ income, setIncome }) {
  return (
    <div className="rounded-2xl pe-surface p-5 space-y-4">
      <Money
        label="Typical take-home amount"
        value={income.take_home_paycheck}
        onChange={(v) => setIncome({ take_home_paycheck: v })}
        testid="income-paycheck-input"
      />
      <div>
        <label className={labelCls}>Pay frequency</label>
        <select
          data-testid="income-frequency-select"
          value={income.pay_frequency}
          onChange={(e) => setIncome({ pay_frequency: e.target.value })}
          className={fieldCls}
        >
          {PAY_FREQUENCIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls}>Next expected payday</label>
        <input
          data-testid="income-payday-input"
          type="date"
          value={income.next_payday}
          onChange={(e) => setIncome({ next_payday: e.target.value })}
          className={fieldCls}
        />
      </div>
      <p className="rounded-xl bg-[#F5F2EC] p-3 text-sm leading-relaxed text-[#70584A]">
        Set this up once. On payday, we'll check what actually arrived.
      </p>
      <WhyWeAsk>This helps establish your normal income rhythm so each Edit starts with better context.</WhyWeAsk>
    </div>
  );
}

function VariableSourceChips({ income, setIncome }) {
  const sel = income.variable_sources || [];
  const toggle = (s) => setIncome({ variable_sources: sel.includes(s) ? sel.filter((x) => x !== s) : [...sel, s] });
  return (
    <div>
      <label className={labelCls}>Where does your variable income come from?</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {VARIABLE_SOURCES.map((s) => {
          const active = sel.includes(s);
          return (
            <button
              key={s}
              type="button"
              data-testid={`variable-source-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => toggle(s)}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition ${
                active ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] bg-white text-[#70584A] hover:border-[#C9A86A]"
              }`}
            >
              {s}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function IncomeDetailsStep({ form, setIncome }) {
  const type = form.income.type;
  return (
    <div className="space-y-6" data-testid="onboarding-income-details">
      {type === "predictable" && (
        <>
          <StepHead eyebrow="Income" title="Tell us about your paycheck">
            <p>Just the basics so we know your normal rhythm.</p>
          </StepHead>
          <PredictableFields income={form.income} setIncome={setIncome} />
        </>
      )}

      {type === "variable" && (
        <>
          <StepHead eyebrow="Income" title="Your income moves. That's fine.">
            <p>
              We will not ask for a pretend "normal paycheck." When money lands, you will enter the actual amount
              available to you. Enter the net usable amount after you have handled your own business expenses and tax
              set-asides. The Paycheck Edit does not calculate taxes.
            </p>
          </StepHead>
          <div className="rounded-2xl pe-surface p-5 space-y-4">
            <VariableSourceChips income={form.income} setIncome={setIncome} />
          </div>
          <div className="rounded-xl bg-[#DDE7DF] p-4 text-sm leading-relaxed text-[#164C3B]">
            Expected money is planning information. Landed money is actionable money.
          </div>
        </>
      )}

      {type === "hybrid" && (
        <>
          <StepHead eyebrow="Income" title="Start with the part you can count on">
            <p>First your predictable baseline, then the money that comes and goes.</p>
          </StepHead>
          <PredictableFields income={form.income} setIncome={setIncome} />
          <div className="rounded-2xl pe-surface p-5 space-y-4">
            <VariableSourceChips income={form.income} setIncome={setIncome} />
            <div>
              <label className={labelCls}>If the variable income stopped for a while, would your regular income cover your essential expenses?</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[["yes", "Yes"], ["mostly", "Mostly"], ["no", "No"]].map(([v, l]) => {
                  const active = form.income.regular_covers_essentials === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      data-testid={`hybrid-cover-${v}`}
                      onClick={() => setIncome({ regular_covers_essentials: v })}
                      className={`rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition ${
                        active ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] bg-white text-[#70584A] hover:border-[#C9A86A]"
                      }`}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-[#F5F2EC] p-3 text-sm leading-relaxed text-[#70584A]">
            We will not treat expected variable income as money that has landed.
          </div>
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// 6. Essentials
// --------------------------------------------------------------------------
export function EssentialsStep({ form, addEssential, updateEssential, removeEssential, setMortgage }) {
  const essentials = form.essentials;
  const mortgage = form.mortgage || { has_mortgage: false, balance: "" };
  const housing = mortgage.has_mortgage ? "mortgage" : "rent";
  const monthlyEssentials = essentials.reduce(
    (s, e) => s + monthlyEquivalent(e.amount, e.frequency || "monthly", e.custom_interval_days), 0);
  return (
    <div className="space-y-6" data-testid="onboarding-essentials">
      <StepHead eyebrow="Essentials" title="Now let's cover the stuff real life expects you to pay.">
        <p>Keep it simple. Add your recurring must-pay obligations like rent, utilities and insurance. Everyday spending like groceries, gas and dining belongs in your Living Bucket, not here. Skip consumer debts too. We collect those next.</p>
      </StepHead>
      <WhyWeAsk>These are the obligations that need to be accounted for before an Edit proposes what the rest of your money can do.</WhyWeAsk>

      {/* Housing: rent vs mortgage. The recurring payment is added as a Housing
          essential below; a mortgage principal balance is tracked separately and
          never enters the debt payoff waterfall. */}
      <div className="rounded-2xl pe-surface p-5 space-y-4" data-testid="housing-block">
        <p className="text-sm font-medium text-[#171717]">Your home: do you rent or have a mortgage?</p>
        <div className="grid grid-cols-2 gap-2">
          {[["rent", "I rent"], ["mortgage", "I have a mortgage"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`housing-${v}`}
              onClick={() => setMortgage && setMortgage(v === "mortgage" ? { has_mortgage: true } : { has_mortgage: false, balance: "" })}
              className={`rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition ${housing === v ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] bg-white text-[#70584A] hover:border-[#C9A86A]"}`}>
              {l}
            </button>
          ))}
        </div>
        {housing === "mortgage" && (
          <>
            <Money label="Current mortgage balance (principal)" value={mortgage.balance}
              onChange={(v) => setMortgage && setMortgage({ balance: v })} testid="mortgage-balance-input"
              hint="We keep this separate from your debt payoff plan. It is never added to the smallest-balance waterfall, and it can prefill a Mortgage Freedom goal later." />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Interest rate</label>
                <div className="relative mt-1">
                  <input inputMode="decimal" value={mortgage.interest_rate || ""} onChange={(e) => setMortgage && setMortgage({ interest_rate: e.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="e.g. 6.5" data-testid="mortgage-rate-input" className={fieldCls + " mt-0 pr-7"} />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#A89A88]">%</span>
                </div>
              </div>
              <Money label="Monthly payment (P&I)" value={mortgage.monthly_payment || ""}
                onChange={(v) => setMortgage && setMortgage({ monthly_payment: v })} testid="mortgage-payment-input" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Years left</label>
                <input inputMode="numeric" value={mortgage.term_months ? String(Math.round(Number(mortgage.term_months) / 12)) : ""}
                  onChange={(e) => { const y = e.target.value.replace(/[^0-9]/g, ""); setMortgage && setMortgage({ term_months: y ? String(parseInt(y, 10) * 12) : "" }); }}
                  placeholder="e.g. 25" data-testid="mortgage-term-input" className={fieldCls + " mt-0"} />
              </div>
            </div>
            <div className="rounded-xl border border-[#C9A86A]/60 bg-[#FBF7EF] p-4" data-testid="mortgage-payment-reminder">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9A7B3A]">Don't forget your monthly payment</p>
              <p className="mt-1.5 text-sm text-[#164C3B]">Add your <span className="font-semibold">full monthly mortgage payment</span> in your Essentials below so The Paycheck Edit reserves it when calculating your next move.</p>
              <p className="mt-1.5 text-xs text-[#70584A]">The optional P&I figure above powers the Mortgage Freedom payoff scenario only. Your real monthly housing obligation may also include taxes, insurance, HOA, or other required costs. Enter the full amount you actually pay as a Housing essential.</p>
            </div>
            <p className="rounded-xl bg-[#F5F2EC] p-3 text-xs leading-relaxed text-[#8A7C6B]">
              Rate, payment and term above are optional and only drive the educational Mortgage Freedom scenario. We never
              duplicate your payment into Essentials automatically, so add it there yourself to avoid double-counting.
            </p>
          </>
        )}
      </div>

      <div className="space-y-3">
        {essentials.map((e) => (
          <div key={e.id} className="rounded-2xl pe-surface p-4 space-y-3" data-testid="essential-row">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className={labelCls}>Bill name</label>
                <input
                  value={e.name}
                  onChange={(ev) => updateEssential(e.id, { name: ev.target.value })}
                  placeholder="e.g. Rent"
                  className={fieldCls + " flex-1"}
                  data-testid="essential-name-input"
                />
              </div>
              <button type="button" onClick={() => removeEssential(e.id)} className="mb-1 rounded-lg p-2 text-[#991B1B] hover:bg-[#F5F2EC]" aria-label="Remove bill" data-testid="essential-remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className={labelCls}>Category</label>
                <select value={e.category} onChange={(ev) => updateEssential(e.id, { category: ev.target.value })} className={fieldCls} data-testid={`essential-category-${e.id}`}>
                  <option value="">Choose one</option>
                  {ESSENTIAL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Amount</label>
                <input
                  inputMode="decimal"
                  value={e.amount}
                  onChange={(ev) => updateEssential(e.id, { amount: ev.target.value.replace(/[^0-9.]/g, "") })}
                  placeholder="$0"
                  className={fieldCls}
                  data-testid="essential-amount-input"
                />
              </div>
              <div>
                <label className={labelCls}>Frequency</label>
                <select value={e.frequency} onChange={(ev) => updateEssential(e.id, { frequency: ev.target.value })} className={fieldCls} data-testid={`essential-frequency-${e.id}`}>
                  <option value="">Choose one</option>
                  {ESSENTIAL_FREQUENCIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {e.frequency === "monthly" ? (
                <div>
                  <label className={labelCls}>Due day</label>
                  <input
                    data-testid="essential-due-day"
                    inputMode="numeric"
                    value={e.due_day}
                    onChange={(ev) => updateEssential(e.id, { due_day: ev.target.value.replace(/[^0-9]/g, "").slice(0, 2) })}
                    placeholder="Day of month (1-31), optional"
                    className={fieldCls}
                  />
                </div>
              ) : e.frequency && e.frequency !== "custom" ? (
                <div>
                  <label className={labelCls}>Next due date</label>
                  <input
                    data-testid="essential-due-date"
                    type="date"
                    value={e.next_due_date}
                    onChange={(ev) => updateEssential(e.id, { next_due_date: ev.target.value })}
                    className={fieldCls}
                  />
                </div>
              ) : null}
              {e.frequency === "custom" && (
                <div>
                  <label className={labelCls}>Every N days</label>
                  <input
                    inputMode="numeric"
                    value={e.custom_interval_days}
                    onChange={(ev) => updateEssential(e.id, { custom_interval_days: ev.target.value.replace(/[^0-9]/g, "").slice(0, 3) })}
                    placeholder="e.g. 30"
                    className={fieldCls}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {monthlyEssentials > 0 && (
        <div className="flex items-center justify-between rounded-xl bg-[#DDE7DF] px-4 py-3 text-sm text-[#164C3B]" data-testid="essentials-monthly-total">
          <span className="font-semibold uppercase tracking-[0.1em]">Monthly essentials</span>
          <span className="font-serif text-lg" data-testid="essentials-monthly-amount">{fmt(Math.round(monthlyEssentials))}</span>
        </div>
      )}
      <Button variant="outline" data-testid="add-essential-button" onClick={addEssential} className="min-h-11 rounded-xl border-[#E5E0D9] bg-white text-[#164C3B] hover:bg-[#F5F2EC]">
        <Plus className="mr-1.5 h-4 w-4" /> Add an essential
      </Button>
    </div>
  );
}

// --------------------------------------------------------------------------
// 7. Living bucket
// --------------------------------------------------------------------------
export function LivingStep({ form, setLiving }) {
  return (
    <div className="space-y-6" data-testid="onboarding-living">
      <StepHead eyebrow="Living Bucket" title="Now give real life a number.">
        <p>
          Groceries, gas, coffee, dinner out and everything in between. One number covers it all.
        </p>
      </StepHead>
      <div className="rounded-2xl pe-surface p-5">
        <Money
          label="Your everyday spending, per income cycle"
          value={form.living_bucket}
          onChange={setLiving}
          testid="living-bucket-input"
          hint="We'll account for this amount when building your Edit, so everyday life still has room. This covers one full income cycle (from one paycheck to the next), not one day."
        />
      </div>
      <WhyWeAsk>This leaves room for everyday spending before The Paycheck Edit proposes what the rest can do.</WhyWeAsk>
    </div>
  );
}

// --------------------------------------------------------------------------
// 8/9. Protection
// --------------------------------------------------------------------------
function RunwayRecommendation({ form }) {
  const [rw, setRw] = useState(null);
  // Recalculate whenever ANY input that affects the recommendation changes:
  // currently protected amount, emergency fund, essentials, living, debts,
  // income type/paycheck and the uncertainty factors. One canonical source
  // (the backend preview) so the input and the card never disagree.
  const payloadKey = JSON.stringify(buildPayload(form));

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      journeyApi.runwayPreview(JSON.parse(payloadKey))
        .then((d) => { if (!cancelled) setRw(d); })
        .catch(() => {});
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [payloadKey]);

  if (!rw || rw.recommended_runway_months == null) return null;
  const gap = rw.protection_gap || 0;
  return (
    <div className="rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="runway-recommendation">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A86A]">Your Protected Runway</p>
      <p className="mt-1 font-serif text-4xl text-[#164C3B]" data-testid="runway-months">{rw.recommended_runway_months} months</p>
      <p className="mt-1 text-sm text-[#70584A]">Based on the Paycheck Edit method and the information you provided.</p>
      <ul className="mt-4 space-y-1.5 text-sm text-[#3f3f3f]">
        {(rw.runway_reasons || []).map((r, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#2F6B57]" /> {r}
          </li>
        ))}
      </ul>
      <div className="mt-4 space-y-1 border-t border-[#EADFC7] pt-4 text-sm">
        <div className="flex justify-between"><span className="text-[#70584A]">Estimated Protected Runway target</span><span className="font-medium text-[#171717]" data-testid="runway-target">{fmt(Math.round(rw.protected_runway_target))}</span></div>
        <div className="flex justify-between"><span className="text-[#70584A]">Currently protected</span><span className="font-medium text-[#171717]">{fmt(Math.round(rw.existing_protected_runway))}</span></div>
        {gap > 0 ? (
          <div className="flex justify-between"><span className="text-[#70584A]">Protection gap</span><span className="font-medium text-[#164C3B]" data-testid="runway-gap">{fmt(Math.round(gap))}</span></div>
        ) : (
          <p className="pt-1 text-[#164C3B]">Your Protected Runway currently meets the Paycheck Edit recommendation. ✓</p>
        )}
      </div>
      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-[#8A7C6B]">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        This is based on the Paycheck Edit methodology and the information you provided. It is not a universal financial
        rule. You decide how much you want to keep protected.
      </p>
    </div>
  );
}

export function ProtectionStep({ form, setIncome, setSafety }) {
  const type = form.income.type;
  const showRunwayQs = type === "variable" || type === "hybrid";
  const inc = form.income;
  return (
    <div className="space-y-6" data-testid="onboarding-protection">
      <StepHead eyebrow="Protection" title="What's already keeping you steady?">
        <p>Some money is for life's surprises. Some money is for keeping life running when income stops. We treat them differently.</p>
      </StepHead>

      <div className="rounded-2xl pe-surface p-5 space-y-4">
        <div className="flex items-center gap-2 text-[#164C3B]"><PiggyBank className="h-5 w-5" /><p className="font-medium">Emergency Fund</p></div>
        <p className="-mt-2 text-sm text-[#70584A]">For spending shocks, like an unexpected repair.</p>
        <Money label="Current Emergency Fund" value={form.safety.emergency_savings} onChange={(v) => setSafety({ emergency_savings: v })} testid="safety-emergency-input" />
      </div>

      <div className="rounded-2xl pe-surface p-5 space-y-4">
        <div className="flex items-center gap-2 text-[#164C3B]"><ShieldCheck className="h-5 w-5" /><p className="font-medium">Protected Runway</p></div>
        <p className="-mt-2 text-sm text-[#70584A]">For income interruption, like a delayed paycheck or temporary loss of income.</p>
        <Money label="Current Protected Runway" value={form.safety.protected_runway} onChange={(v) => setSafety({ protected_runway: v })} testid="safety-runway-input"
          hint="Do not count retirement accounts, home equity, available credit, or inaccessible investments." />
      </div>

      {showRunwayQs && (
        <div className="rounded-2xl pe-surface p-5 space-y-3" data-testid="runway-factors">
          <p className="text-sm font-medium text-[#171717]">A few questions to size your Protected Runway.</p>
          {[
            ["income_timing_uncertain", "Is the timing of your next income uncertain?"],
            ["income_history_inconsistent", "Is your income history new, sporadic, or highly inconsistent?"],
          ].map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#F5F2EC] p-3" data-testid={`factor-${key}`}>
              <input
                type="checkbox"
                checked={!!inc[key]}
                onChange={(e) => setIncome({ [key]: e.target.checked })}
                className="mt-0.5 h-5 w-5 rounded border-[#E5E0D9] text-[#2F6B57] focus:ring-[#2F6B57]"
              />
              <span className="text-sm text-[#3f3f3f]">{label}</span>
            </label>
          ))}
          {type === "variable" && (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#F5F2EC] p-3" data-testid="factor-no_other_household_income">
              <input
                type="checkbox"
                checked={!!inc.no_other_household_income}
                onChange={(e) => setIncome({ no_other_household_income: e.target.checked })}
                className="mt-0.5 h-5 w-5 rounded border-[#E5E0D9] text-[#2F6B57] focus:ring-[#2F6B57]"
              />
              <span className="text-sm text-[#3f3f3f]">Is there no other reliable household income that sufficiently covers your essential expenses?</span>
            </label>
          )}
        </div>
      )}

      {/* Every income type receives a Protected Runway recommendation. Income
          stability changes the recommended number of months, not whether a
          recommendation exists. */}
      <RunwayRecommendation form={form} />

      {!showRunwayQs && (
        <p className="rounded-xl bg-[#F5F2EC] p-3 text-sm leading-relaxed text-[#70584A]" data-testid="protection-predictable-note">
          Because your income is predictable, this runway is a recommendation, not a required step. Your protected
          savings stay out of every Edit calculation and The Paycheck Edit never spends them for you.
        </p>
      )}

      <p className="rounded-xl bg-[#DDE7DF] p-3 text-sm leading-relaxed text-[#164C3B]" data-testid="protection-trust-note">
        The Paycheck Edit does not hold or move this money. Amounts you designate as protected stay outside proposed allocations.
      </p>
      <WhyWeAsk>This helps keep the money you've designated for emergencies or income interruption outside proposed allocations.</WhyWeAsk>
    </div>
  );
}

// --------------------------------------------------------------------------
// 10. Debts
// --------------------------------------------------------------------------
export function DebtsStep({ form, addDebt, updateDebt, removeDebt, onNoDebt }) {
  const debts = form.debts;
  const total = debts.reduce((s, d) => s + num(d.balance), 0);
  return (
    <div className="space-y-6" data-testid="onboarding-debts">
      <StepHead eyebrow="Debts" title="Let's list the debts you want to knock out.">
        <p>
          We use this information to help you explore eligible consumer-debt payoff decisions. No account numbers or
          bank logins needed. Mortgages are not part of this list.
        </p>
      </StepHead>
      <WhyWeAsk>This helps The Paycheck Edit understand which eligible consumer debt should be considered during Eliminate.</WhyWeAsk>
      <div className="flex items-center justify-between rounded-xl bg-[#DDE7DF] px-4 py-3 text-sm text-[#164C3B]" data-testid="debts-running-total">
        <span>{debts.length} {debts.length === 1 ? "debt" : "debts"}</span>
        <span className="font-medium">{fmt(total)} total</span>
      </div>
      <div className="space-y-3">
        {debts.map((d) => (
          <div key={d.id} className="rounded-2xl pe-surface p-4 space-y-3" data-testid="debt-row">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className={labelCls}>Debt name</label>
                <input
                  value={d.name}
                  onChange={(ev) => updateDebt(d.id, { name: ev.target.value })}
                  placeholder="e.g. Chase card"
                  className={fieldCls + " flex-1"}
                  data-testid="debt-name-input"
                />
              </div>
              <button type="button" onClick={() => removeDebt(d.id)} className="mb-1 rounded-lg p-2 text-[#991B1B] hover:bg-[#F5F2EC]" aria-label="Remove debt" data-testid="debt-remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Debt type</label>
                <select value={d.debt_type} onChange={(ev) => updateDebt(d.id, { debt_type: ev.target.value })} className={fieldCls} data-testid={`debt-type-${d.id}`}>
                  <option value="">Choose one</option>
                  {DEBT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Remaining balance</label>
                <input inputMode="decimal" value={d.balance} onChange={(ev) => updateDebt(d.id, { balance: ev.target.value.replace(/[^0-9.]/g, "") })} placeholder="$0" className={fieldCls} data-testid="debt-balance-input" />
              </div>
              <div>
                <label className={labelCls}>Monthly payment</label>
                <input inputMode="decimal" value={d.minimum_payment} onChange={(ev) => updateDebt(d.id, { minimum_payment: ev.target.value.replace(/[^0-9.]/g, "") })} placeholder="$0" className={fieldCls} data-testid="debt-minimum-input" />
              </div>
              <div>
                <label className={labelCls}>Payment due day</label>
                <input inputMode="numeric" value={d.minimum_due_day} onChange={(ev) => updateDebt(d.id, { minimum_due_day: ev.target.value.replace(/[^0-9]/g, "").slice(0, 2) })} placeholder="Day of month (1-31)" className={fieldCls} data-testid="debt-due-day-input" />
              </div>
              <div>
                <label className={labelCls}>APR (optional)</label>
                <input inputMode="decimal" value={d.apr} onChange={(ev) => updateDebt(d.id, { apr: ev.target.value.replace(/[^0-9.]/g, "") })} placeholder="Not entered" className={fieldCls} data-testid="debt-apr-input" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" data-testid="add-debt-button" onClick={addDebt} className="min-h-11 rounded-xl border-[#E5E0D9] bg-white text-[#164C3B] hover:bg-[#F5F2EC]">
        <Plus className="mr-1.5 h-4 w-4" /> Add a debt
      </Button>
      {debts.length === 0 && onNoDebt && (
        <button
          type="button"
          data-testid="no-debt-button"
          onClick={onNoDebt}
          className="w-full rounded-xl border border-dashed border-[#C9A86A]/70 bg-[#FBF9F4] px-4 py-3 text-sm font-semibold uppercase tracking-[0.08em] text-[#164C3B] transition hover:bg-[#F5F2EC]"
        >
          I don't have any eligible consumer debt
        </button>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// 11. Review
// --------------------------------------------------------------------------
function ReviewRow({ label, value, onEdit, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl pe-surface px-4 py-3" data-testid={testid}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">{label}</p>
        <p className="mt-0.5 text-sm font-medium text-[#171717]">{value}</p>
      </div>
      <button type="button" onClick={onEdit} className="text-sm font-medium text-[#2F6B57] hover:underline" data-testid={`${testid}-edit`}>Edit</button>
    </div>
  );
}

export function ReviewStep({ form, goTo, onLock, saving }) {
  const inc = form.income;
  const typeLabel = (INCOME_TYPE_CARDS.find((c) => c.value === inc.type) || {}).title || "Income";
  const freqLabel = (PAY_FREQUENCIES.find((f) => f.value === inc.pay_frequency) || {}).label || "";
  const incomeValue = inc.type === "variable"
    ? "Variable. Entered when money lands."
    : `${fmt(num(inc.take_home_paycheck))}${freqLabel ? ` · ${freqLabel}` : ""}`;
  const essTotal = form.essentials.filter((e) => num(e.amount) > 0).reduce((s, e) => s + num(e.amount), 0);
  const debtTotal = form.debts.reduce((s, d) => s + num(d.balance), 0);
  const minTotal = form.debts.reduce((s, d) => s + num(d.minimum_payment), 0);

  return (
    <div className="space-y-6" data-testid="onboarding-review">
      <StepHead eyebrow="Review" title="Here's your foundation.">
        <p>Give it a look. You can edit any section. We are not calculating anything yet.</p>
      </StepHead>
      <div className="space-y-3">
        <ReviewRow label="Income" value={`${typeLabel} · ${incomeValue}`} onEdit={() => goTo("incomeType")} testid="review-income" />
        <ReviewRow label="Essentials" value={`${form.essentials.filter((e) => e.name.trim() && num(e.amount) > 0).length} items · ${fmt(essTotal)}`} onEdit={() => goTo("essentials")} testid="review-essentials" />
        <ReviewRow label="Living Bucket" value={fmt(num(form.living_bucket))} onEdit={() => goTo("living")} testid="review-living" />
        <ReviewRow label="Protection" value={`Emergency ${fmt(num(form.safety.emergency_savings))} · Runway ${fmt(num(form.safety.protected_runway))}`} onEdit={() => goTo("protection")} testid="review-protection" />
        <ReviewRow label="Debts" value={`${form.debts.filter((d) => d.name.trim()).length} debts · ${fmt(debtTotal)} · ${fmt(minTotal)}/mo min`} onEdit={() => goTo("debts")} testid="review-debts" />
      </div>
      <Button
        data-testid="lock-foundation-button"
        onClick={onLock}
        disabled={saving}
        className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]"
      >
        {saving ? "Saving…" : "LOCK IN MY FOUNDATION"}
        {!saving && <CheckCircle2 className="ml-1.5 h-4 w-4" />}
      </Button>
    </div>
  );
}

// --------------------------------------------------------------------------
// 12. Complete
// --------------------------------------------------------------------------
export function CompleteStep({ onYes, onNot }) {
  return (
    <div className="space-y-6 text-center" data-testid="onboarding-complete">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#DDE7DF] text-3xl">🎉</span>
      <div>
        <h1 className="font-serif text-4xl leading-tight text-[#171717]">Your money finally has a starting point.</h1>
        <p className="mt-3 text-lg leading-relaxed text-[#3f3f3f]">
          Your Foundation is ready. Now The Paycheck Edit can account for what must be covered, what needs protecting,
          and what you're working toward.
        </p>
      </div>
      <div className="rounded-2xl pe-surface p-6">
        <p className="font-serif text-2xl text-[#164C3B]">Feeling rich?</p>
        <p className="mt-1 text-sm text-[#70584A]">If money came in today, start your first Edit.</p>
        <div className="mt-5 space-y-3">
          <Button
            data-testid="complete-yes-sprint"
            onClick={onYes}
            className="min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold tracking-wide text-white hover:bg-[#255643]"
          >
            START MY FIRST EDIT <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <Button
            data-testid="complete-not-yet"
            variant="outline"
            onClick={onNot}
            className="min-h-12 w-full rounded-xl border-[#E5E0D9] bg-white text-base font-semibold tracking-wide text-[#164C3B] hover:bg-[#F5F2EC]"
          >
            NOT YET. TAKE ME TO MY DASHBOARD
          </Button>
        </div>
      </div>
    </div>
  );
}
